import { useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
// Api
import { SUGGEST_USERNAMES } from 'api/auth/queries';
// Types
import {
  SuggestUsernames,
  SuggestUsernamesVariables,
} from 'api/auth/types/SuggestUsernames';

type UseSuggestUsernameParams = {
  firstName?: string | null;
  lastName?: string | null;
  // Username that is already saved for the user, no suggestion is needed then
  existingUsername?: string | null;
  enabled?: boolean;
  getCurrentUsername: () => string | undefined;
  onSuggest: (username: string) => void;
};

// Prefills an empty username field with the first suggestion from the BE.
// The suggestion is not applied if the user typed something in the meantime.
export const useSuggestUsername = ({
  firstName,
  lastName,
  existingUsername,
  enabled = true,
  getCurrentUsername,
  onSuggest,
}: UseSuggestUsernameParams) => {
  const [suggestUsernames] = useLazyQuery<
    SuggestUsernames,
    SuggestUsernamesVariables
  >(SUGGEST_USERNAMES);

  useEffect(() => {
    if (!enabled || existingUsername || !firstName || !lastName) {
      return;
    }

    let cancelled = false;

    const fetchSuggestion = async () => {
      try {
        const { data } = await suggestUsernames({
          variables: { input: { firstName, lastName } },
        });
        const suggestion = data?.suggestUsernames?.suggestions?.[0];

        if (!cancelled && suggestion && !getCurrentUsername()) {
          onSuggest(suggestion);
        }
      } catch {
        // Suggestion is optional, user can still type the username manually
      }
    };

    fetchSuggestion();

    return () => {
      cancelled = true;
    };
    // getCurrentUsername and onSuggest are intentionally omitted, they are
    // read when the response arrives and should not retrigger the request
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, existingUsername, firstName, lastName, suggestUsernames]);
};
